import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axiosConn from "config/axiosConn";
import ReactApexChart from "react-apexcharts";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./ThemesAnalyticsDashboard.css"; // Reuse central dashboard styles

// ─── Constants ──────────────────────────────────────────────────────────────────
// Themes Analytics only shows data from June 5, 2026 onward.
const MIN_THEMES_DATE = "2026-06-05";

const THEME_ICONS = {
  "Adopt Healthy Lifestyles":       "🏃",
  "Adopt Sustainable Food Systems": "🌱",
  "Reduce Waste":                   "♻️",
  "Reduce E-Waste":                 "💻",
  "Save Energy":                    "⚡",
  "Save Water":                     "💧",
  "Say No to Single Use Plastic":   "🚫",
};

const THEME_COLORS = {
  "Adopt Healthy Lifestyles":       "#2563eb",
  "Adopt Sustainable Food Systems": "#0d9488",
  "Reduce Waste":                   "#ea580c",
  "Reduce E-Waste":                 "#7c3aed",
  "Save Energy":                    "#d97706",
  "Save Water":                     "#0891b2",
  "Say No to Single Use Plastic":   "#dc2626",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────────
const fmt = (n) => {
  if (!n && n !== 0) return "—";
  if (n >= 10_000_000) return (n / 10_000_000).toFixed(1) + "Cr";
  if (n >= 100_000)    return (n / 100_000).toFixed(1) + "L";
  if (n >= 1_000)      return n.toLocaleString("en-IN");
  return n.toString();
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

// ─── Skeleton Loader ─────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div style={{
      background: "#f1f5f9",
      borderRadius: 12,
      padding: 20,
      animation: "pulse 1.5s ease-in-out infinite",
      height: 120,
    }}>
      <div style={{ background: "#e2e8f0", borderRadius: 6, height: 14, width: "60%", marginBottom: 12 }} />
      <div style={{ background: "#e2e8f0", borderRadius: 6, height: 28, width: "40%", marginBottom: 8 }} />
      <div style={{ background: "#e2e8f0", borderRadius: 6, height: 10, width: "80%" }} />
    </div>
  );
}

// ─── KPI Card ────────────────────────────────────────────────────────────────────
function KpiCard({ icon, value, label, color = "#3b82f6" }) {
  return (
    <div className="tad-kpi-card" style={{ "--kpi-color": color }}>
      <span className="tad-kpi-icon">{icon}</span>
      <div className="tad-kpi-value">{fmt(value)}</div>
      <div className="tad-kpi-label">{label}</div>
    </div>
  );
}

// ─── Theme Detail Modal (sub-themes + KPIs for a single theme) ─────────────────
function ThemeDetailModal({ theme, onClose }) {
  const color    = theme.color || THEME_COLORS[theme.theme] || "#3b82f6";
  const icon     = THEME_ICONS[theme.theme] || "🌿";
  const subThemes = (theme.subThemes || []).filter((s) => s.subTheme && s.subTheme !== "null");
  const maxReg   = subThemes[0]?.registrations || 1;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{
        background: "#fff", borderRadius: 18, width: "100%", maxWidth: 780,
        maxHeight: "88vh", display: "flex", flexDirection: "column",
        boxShadow: "0 32px 64px rgba(0,0,0,0.22)", overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${color}ee, ${color}99)`,
          padding: "22px 28px 18px",
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        }}>
          <div>
            <div style={{ fontSize: 28, lineHeight: 1 }}>{icon}</div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 20, marginTop: 8 }}>{theme.theme}</div>
            <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 13, marginTop: 2 }}>Theme Detail — State Scope</div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.22)", border: "none", borderRadius: 10,
              padding: "8px 16px", color: "#fff", cursor: "pointer", fontSize: 15, fontWeight: 700,
            }}
          >✕</button>
        </div>

        {/* KPI strip */}
        <div style={{
          display: "flex", gap: 0, borderBottom: "1px solid #f1f5f9",
          background: "#fafbfc",
        }}>
          {[
            { label: "Events",       val: theme.registrations, ic: "📅" },
            { label: "Schools",      val: theme.schools,       ic: "🏫" },
            { label: "Districts",    val: theme.districts,     ic: "🏗️" },
            { label: "Participants", val: theme.participants,  ic: "👥" },
            { label: "% Share",      val: theme.adoptionPct + "%", ic: "📊" },
          ].map((k) => (
            <div key={k.label} style={{
              flex: 1, padding: "14px 8px", textAlign: "center", borderRight: "1px solid #f1f5f9",
            }}>
              <div style={{ fontSize: 18 }}>{k.ic}</div>
              <div style={{ fontWeight: 800, fontSize: 18, color, marginTop: 2 }}>
                {typeof k.val === "number" ? fmt(k.val) : k.val}
              </div>
              <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>{k.label}</div>
            </div>
          ))}
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", flex: 1, padding: "20px 28px 28px" }}>
          {subThemes.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px 0", color: "#9ca3af", fontSize: 15 }}>
              📦 No sub-theme data available for this theme in your state.
            </div>
          ) : (
            <>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b", marginBottom: 14 }}>
                🔍 Sub-Themes ({subThemes.length})
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: color + "12" }}>
                    <th style={{ padding: "10px 14px", textAlign: "left",  color, fontWeight: 700 }}>#</th>
                    <th style={{ padding: "10px 14px", textAlign: "left",  color, fontWeight: 700 }}>Sub-Theme</th>
                    <th style={{ padding: "10px 14px", textAlign: "right", color, fontWeight: 700 }}>Events</th>
                    <th style={{ padding: "10px 14px", textAlign: "right", color, fontWeight: 700 }}>Schools</th>
                    <th style={{ padding: "10px 14px", textAlign: "right", color, fontWeight: 700 }}>Districts</th>
                    <th style={{ padding: "10px 14px", textAlign: "right", color, fontWeight: 700 }}>Participants</th>
                    <th style={{ padding: "10px 14px", textAlign: "left",  color, fontWeight: 700 }}>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {subThemes.map((st, i) => {
                    const pct = maxReg > 0 ? Math.round((st.registrations / maxReg) * 100) : 0;
                    return (
                      <tr key={i} style={{
                        borderBottom: "1px solid #f1f5f9",
                        background: i % 2 === 0 ? "#fff" : "#fafbfc",
                      }}>
                        <td style={{ padding: "10px 14px", color: "#94a3b8", fontWeight: 600 }}>{i + 1}</td>
                        <td style={{ padding: "10px 14px", fontWeight: 600, color: "#1e293b", maxWidth: 220 }}>
                          {st.subTheme || "General"}
                        </td>
                        <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color }}>
                          {fmt(st.registrations)}
                        </td>
                        <td style={{ padding: "10px 14px", textAlign: "right", color: "#475569" }}>
                          {fmt(st.schools)}
                        </td>
                        <td style={{ padding: "10px 14px", textAlign: "right", color: "#475569" }}>
                          {fmt(st.districts)}
                        </td>
                        <td style={{ padding: "10px 14px", textAlign: "right", color: "#475569" }}>
                          {fmt(st.participants)}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ flex: 1, background: "#e2e8f0", borderRadius: 4, height: 6 }}>
                              <div style={{ width: `${pct}%`, background: color, borderRadius: 4, height: 6 }} />
                            </div>
                            <span style={{ fontSize: 11, color: "#64748b", minWidth: 30 }}>{pct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Theme Card ──────────────────────────────────────────────────────────────────
function StateThemeCard({ theme, totalRegs, onDrillDown }) {
  const color = theme.color || THEME_COLORS[theme.theme] || "#3b82f6";
  const icon  = THEME_ICONS[theme.theme] || "🌿";
  const pct   = totalRegs > 0 ? +((theme.registrations / totalRegs) * 100).toFixed(1) : 0;

  return (
    <div
      className="tad-theme-card"
      style={{ "--theme-color": color, "--theme-color-glow": color + "18" }}
    >
      <div className="tad-theme-header">
        <div className="tad-theme-name">{icon} {theme.theme}</div>
        <div className="tad-theme-rank">
          <span className="tad-theme-rank-num">#{theme.rank}</span>
          <span className="tad-theme-rank-label">Rank</span>
        </div>
      </div>

      <div className="tad-theme-metrics">
        <div className="tad-theme-metric">
          <div className="tad-theme-metric-val">{fmt(theme.registrations)}</div>
          <div className="tad-theme-metric-lbl">Events</div>
        </div>
        <div className="tad-theme-metric">
          <div className="tad-theme-metric-val">{fmt(theme.districts)}</div>
          <div className="tad-theme-metric-lbl">Districts</div>
        </div>
        <div className="tad-theme-metric">
          <div className="tad-theme-metric-val">{fmt(theme.schools)}</div>
          <div className="tad-theme-metric-lbl">Schools</div>
        </div>
        <div className="tad-theme-metric">
          <div className="tad-theme-metric-val">{pct}%</div>
          <div className="tad-theme-metric-lbl">% Share</div>
        </div>
      </div>

      <div className="tad-adoption-bar-wrap">
        <div className="tad-adoption-bar-header">
          <span className="tad-adoption-bar-label">Contribution</span>
          <span className="tad-adoption-bar-pct">{pct}%</span>
        </div>
        <div className="tad-adoption-bar-bg">
          <div className="tad-adoption-bar-fill" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
        </div>
      </div>

      <div className="tad-theme-card-footer">
        <span
          className="tad-view-link"
          style={{ color }}
          onClick={() => onDrillDown && onDrillDown(theme.theme)}
        >
          Detail →
        </span>
      </div>
    </div>
  );
}

// ─── Rank Badge ───────────────────────────────────────────────────────────────────
function RankBadge({ idx }) {
  const cls = idx === 0 ? "rank-1" : idx === 1 ? "rank-2" : idx === 2 ? "rank-3" : "rank-other";
  const medals = ["🥇", "🥈", "🥉"];
  return <span className={`tad-rank-badge ${cls}`}>{idx < 3 ? medals[idx] : idx + 1}</span>;
}

// ─── District Drilldown Modal ─────────────────────────────────────────────────────
function DistrictModal({ district, schools, onClose }) {
  const [search, setSearch] = useState("");
  const filtered = (schools || []).filter((s) =>
    (s.schoolName || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, maxWidth: 700, width: "100%",
        maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 25px 50px rgba(0,0,0,0.25)",
      }}>
        <div style={{
          padding: "20px 24px 14px", borderBottom: "1px solid #e5e7eb",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: "linear-gradient(135deg, #0d9488, #2563eb)", borderRadius: "16px 16px 0 0",
        }}>
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>🏛️ {district}</div>
            <div style={{ color: "#a7f3d0", fontSize: 13, marginTop: 2 }}>District School Details</div>
          </div>
          <button
            onClick={onClose}
            style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 8, padding: "6px 14px", color: "#fff", cursor: "pointer", fontSize: 15 }}
          >✕</button>
        </div>

        <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
          <input
            placeholder="🔍 Search school..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%", padding: "8px 14px", borderRadius: 8,
              border: "1.5px solid #e5e7eb", fontSize: 14, outline: "none",
            }}
          />
        </div>

        <div style={{ overflowY: "auto", flex: 1, padding: "0 4px" }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
              {schools?.length === 0 ? "No school data for this district." : "No results found."}
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead style={{ position: "sticky", top: 0, background: "#f8fafc" }}>
                <tr>
                  <th style={{ padding: "10px 16px", textAlign: "left", color: "#64748b", fontWeight: 600 }}>#</th>
                  <th style={{ padding: "10px 16px", textAlign: "left", color: "#64748b", fontWeight: 600 }}>School Name</th>
                  <th style={{ padding: "10px 16px", textAlign: "right", color: "#64748b", fontWeight: 600 }}>Events</th>
                  <th style={{ padding: "10px 16px", textAlign: "right", color: "#64748b", fontWeight: 600 }}>Themes</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "9px 16px", color: "#94a3b8" }}>{i + 1}</td>
                    <td style={{ padding: "9px 16px", fontWeight: 500, color: "#1e293b" }}>{s.schoolName || "—"}</td>
                    <td style={{ padding: "9px 16px", textAlign: "right", fontWeight: 700, color: "#0d9488" }}>{fmt(s.registrations)}</td>
                    <td style={{ padding: "9px 16px", textAlign: "right", color: "#7c3aed" }}>{s.themesParticipated || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────────
export default function StateThemesAnalyticsDashboard() {
  const navigate   = useNavigate();
  const userData   = useSelector((state) => state.auth.user);

  // ── State ──────────────────────────────────────────────────────────────────────
  const [summary,         setSummary]         = useState(null);
  const [themeData,       setThemeData]       = useState(null);
  const [districtData,    setDistrictData]    = useState(null);
  const [loadingSummary,  setLoadingSummary]  = useState(true);
  const [loadingThemes,   setLoadingThemes]   = useState(true);
  const [loadingDistrict, setLoadingDistrict] = useState(true);
  const [error,           setError]           = useState(null);
  const [fromDate,          setFromDate]          = useState(MIN_THEMES_DATE);
  const [toDate,            setToDate]            = useState("");

  // Search & Sort
  const [themeSearch,    setThemeSearch]    = useState("");
  const [districtSearch, setDistrictSearch] = useState("");
  const [districtSort,   setDistrictSort]   = useState("registrations"); // "registrations" | "schools" | "themesCovered"

  // District drilldown modal
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [districtSchools,  setDistrictSchools]  = useState([]);
  const [loadingSchools,   setLoadingSchools]   = useState(false);

  // stateName: prefer API response (backend resolves from JWT/username) over redux userData
  const stateName = summary?.state || themeData?.state || userData?.state || "—";

  // Selected theme for inline detail modal
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [downloadingReport, setDownloadingReport] = useState(false);

  // ── Data Fetching ──────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoadingSummary(true);
    setLoadingThemes(true);
    setLoadingDistrict(true);
    setError(null);
    try {
      const [sumRes, themeRes, districtRes] = await Promise.all([
        axiosConn("post", "themes/state-summary",           { fromDate, toDate }),
        axiosConn("post", "themes/state-analysis",          { fromDate, toDate }),
        axiosConn("post", "themes/state-district-analysis", { fromDate, toDate }),
      ]);
      if (sumRes?.data?.success)      setSummary(sumRes.data);
      if (themeRes?.data?.success)    setThemeData(themeRes.data);
      if (districtRes?.data?.success) setDistrictData(districtRes.data);
    } catch (err) {
      console.error("State Dashboard fetch error:", err);
      setError(err?.response?.data?.error || "Network error — please try again.");
    } finally {
      setLoadingSummary(false);
      setLoadingThemes(false);
      setLoadingDistrict(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // District drilldown — fetch school-wise data
  const handleDistrictClick = useCallback(async (districtName) => {
    setSelectedDistrict(districtName);
    setDistrictSchools([]);
    setLoadingSchools(true);
    try {
      const res = await axiosConn("post", "themes/state-district-analysis", { district: districtName, fromDate, toDate });
      if (res?.data?.success) setDistrictSchools(res.data.schools || []);
    } catch {
      setDistrictSchools([]);
    } finally {
      setLoadingSchools(false);
    }
  }, [fromDate, toDate]);

  // ── Derived data ───────────────────────────────────────────────────────────────
  const filteredThemes = useMemo(() => {
    if (!themeData?.themes) return [];
    const q = themeSearch.toLowerCase();
    return themeData.themes.filter((t) => t.theme.toLowerCase().includes(q));
  }, [themeData, themeSearch]);

  const sortedDistricts = useMemo(() => {
    if (!districtData?.districts) return [];
    let list = [...districtData.districts];
    const q = districtSearch.toLowerCase();
    if (q) list = list.filter((d) => d.district.toLowerCase().includes(q));
    list.sort((a, b) => (b[districtSort] || 0) - (a[districtSort] || 0));
    return list;
  }, [districtData, districtSearch, districtSort]);

  // ── Charts ────────────────────────────────────────────────────────────────────
  const donutOptions = useMemo(() => {
    if (!themeData?.themes) return null;
    const filtered = themeData.themes.filter((t) => t.registrations > 0);
    return {
      options: {
        chart: { type: "donut", background: "transparent", toolbar: { show: false } },
        labels: filtered.map((t) => t.theme),
        colors: filtered.map((t) => t.color || THEME_COLORS[t.theme] || "#6b7280"),
        legend: { position: "bottom", labels: { colors: "#495057" }, fontSize: "11px" },
        plotOptions: {
          pie: { donut: { size: "65%", labels: { show: true, total: {
            show: true, label: "Total Events", fontSize: "12px", color: "#6c757d",
            formatter: () => fmt(themeData.totalRegistrations || 0),
          }}}},
        },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v) => `${v} events` } },
        theme: { mode: "light" },
      },
      series: filtered.map((t) => t.registrations),
    };
  }, [themeData]);

  const schoolBarOptions = useMemo(() => {
    if (!themeData?.themes) return null;
    const sorted = [...themeData.themes].sort((a, b) => b.schools - a.schools);
    return {
      options: {
        chart: { type: "bar", background: "transparent", toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: "60%", distributed: true, borderRadius: 4 } },
        colors: sorted.map((t) => t.color || THEME_COLORS[t.theme] || "#6b7280"),
        xaxis: {
          categories: sorted.map((t) => t.theme.length > 22 ? t.theme.slice(0, 20) + "…" : t.theme),
          labels: { style: { colors: "#495057", fontSize: "11px" } },
        },
        legend: { show: false },
        dataLabels: { enabled: true, style: { fontSize: "11px", colors: ["#ffffff"] }, formatter: fmt },
        grid: { borderColor: "#e9ecef" },
        theme: { mode: "light" },
      },
      series: [{ name: "Schools", data: sorted.map((t) => t.schools) }],
    };
  }, [themeData]);

  const districtBarOptions = useMemo(() => {
    if (!districtData?.districts?.length) return null;
    const top10 = [...districtData.districts].slice(0, 10);
    return {
      options: {
        chart: { type: "bar", background: "transparent", toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: "65%", borderRadius: 4 } },
        colors: ["#0d9488"],
        xaxis: {
          categories: top10.map((d) => d.district.length > 20 ? d.district.slice(0, 18) + "…" : d.district),
          labels: { style: { colors: "#495057", fontSize: "11px" } },
        },
        legend: { show: false },
        dataLabels: { enabled: true, style: { fontSize: "10px", colors: ["#ffffff"] }, formatter: fmt },
        grid: { borderColor: "#e9ecef" },
        theme: { mode: "light" },
      },
      series: [{ name: "Events", data: top10.map((d) => d.registrations) }],
    };
  }, [districtData]);

  const trendOptions = useMemo(() => {
    if (!themeData?.trend?.length) return null;
    return {
      options: {
        chart: { type: "area", background: "transparent", toolbar: { show: false }, zoom: { enabled: false } },
        colors: ["#28a745"],
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 100] } },
        stroke: { curve: "smooth", width: 2 },
        xaxis: {
          categories: themeData.trend.map((d) => fmtDate(d._id)),
          labels: { style: { colors: "#6c757d", fontSize: "10px" }, rotate: -30 },
          axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: "#6c757d", fontSize: "10px" }, formatter: fmt } },
        grid: { borderColor: "#e9ecef", strokeDashArray: 4 },
        dataLabels: { enabled: false },
        theme: { mode: "light" },
      },
      series: [{ name: "Events", data: themeData.trend.map((d) => d.registrations) }],
    };
  }, [themeData]);

  // ── Export Excel ──────────────────────────────────────────────────────────────
  const exportExcel = useCallback(() => {
    const wb = XLSX.utils.book_new();

    // Summary sheet
    if (summary) {
      const sumSheet = XLSX.utils.aoa_to_sheet([
        ["State/UT/AB", summary.state],
        ["Districts Participated", summary.districtsCount],
        ["Schools Participated", summary.schoolsCount],
        ["Total Events", summary.registrationsCount],
        ["Themes Active", summary.themesCount],
        ["Sub-Themes Active", summary.subThemesCount],
      ]);
      XLSX.utils.book_append_sheet(wb, sumSheet, "State Summary");
    }

    // Themes sheet
    if (themeData?.themes) {
      const themeRows = themeData.themes.map((t) => [
        t.theme, t.registrations, t.districts, t.schools, t.adoptionPct + "%",
      ]);
      const themeSheet = XLSX.utils.aoa_to_sheet([
        ["Theme", "Events", "Districts", "Schools", "% Share"],
        ...themeRows,
      ]);
      XLSX.utils.book_append_sheet(wb, themeSheet, "Theme Analysis");
    }

    // Districts sheet
    if (districtData?.districts) {
      const distRows = districtData.districts.map((d, i) => [
        i + 1, d.district, d.schools, d.registrations, d.themesCovered,
      ]);
      const distSheet = XLSX.utils.aoa_to_sheet([
        ["#", "District", "Schools", "Events", "Themes Covered"],
        ...distRows,
      ]);
      XLSX.utils.book_append_sheet(wb, distSheet, "District Analysis");
    }

    XLSX.writeFile(wb, `${stateName}_Themes_Analysis_${new Date().toISOString().split("T")[0]}.xlsx`);
  }, [summary, themeData, districtData, stateName]);

  // ── Export PDF (Backend Scoped State Report) ──────────────────────────────────
  const handleDownloadReport = async () => {
    if (downloadingReport) return;
    setDownloadingReport(true);
    try {
      const queryParams = new URLSearchParams();
      if (fromDate) queryParams.append("fromDate", fromDate);
      if (toDate) queryParams.append("toDate", toDate);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL || "https://ecoclubs.education.gov.in/"}/api/reports/state-themes?${queryParams.toString()}`,
        {
          method: "GET",
          credentials: "include",
        }
      );
      if (!response.ok) throw new Error("Failed to generate report");
      const blob = await response.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `${stateName.replace(/\s+/g, "_")}_Themes_Report.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("State report download error:", err);
      alert("Failed to download the State Themes Analytics Report. Please try again.");
    } finally {
      setDownloadingReport(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────────
  const isLoading = loadingSummary || loadingThemes || loadingDistrict;

  return (
    <div className="tad-page">
      {/* ── HEADER ── */}
      <div className="tad-header">
        <div className="tad-header-left">
          <div className="tad-badge">
            <span className="dot" /> State Dashboard
          </div>
          <h1>🌿 Themes Analysis Dashboard</h1>
          <p className="tad-header-sub">
            Mission LiFE · Eco Clubs · All 7 Themes · Scope: <strong style={{ color: "#0d9488" }}>{stateName.toUpperCase()}</strong>
          </p>
        </div>

        <div className="tad-header-actions" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <button className="tad-btn tad-btn-ghost" onClick={() => navigate("/admin/environment-day-dashboard")}>← Back</button>
            <button className="tad-btn tad-btn-primary" onClick={fetchAll} disabled={isLoading}>
              {isLoading ? "⏳ Loading…" : "🔄 Refresh"}
            </button>
            <button className="tad-btn" style={{ background: "#dc2626", color: "#fff", border: "none" }} onClick={handleDownloadReport} disabled={isLoading || downloadingReport}>
              {downloadingReport ? "⏳ Generating..." : "📄 Export PDF"}
            </button>
          </div>

          <div className="tad-date-filters" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <label style={{ fontSize: "10px", fontWeight: "600", color: "#64748b", marginBottom: "2px" }}>From Date</label>
              <input
                type="date"
                value={fromDate}
                min={MIN_THEMES_DATE}
                onChange={(e) => setFromDate(e.target.value)}
                style={{
                  padding: "5px 10px",
                  borderRadius: "6px",
                  border: "1.5px solid #e2e8f0",
                  fontSize: "12px",
                  outline: "none",
                  color: "#334155"
                }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <label style={{ fontSize: "10px", fontWeight: "600", color: "#64748b", marginBottom: "2px" }}>To Date</label>
              <input
                type="date"
                value={toDate}
                min={MIN_THEMES_DATE}
                onChange={(e) => setToDate(e.target.value)}
                style={{
                  padding: "5px 10px",
                  borderRadius: "6px",
                  border: "1.5px solid #e2e8f0",
                  fontSize: "12px",
                  outline: "none",
                  color: "#334155"
                }}
              />
            </div>
            {(fromDate !== MIN_THEMES_DATE || toDate) && (
              <button
                onClick={() => {
                  setFromDate(MIN_THEMES_DATE);
                  setToDate("");
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#dc2626",
                  fontSize: "11px",
                  fontWeight: "700",
                  cursor: "pointer",
                  marginTop: "14px",
                  padding: "0"
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="tad-body">
        {/* Error State */}
        {error && !isLoading && (
          <div className="tad-error">
            <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Failed to load data</div>
            <div style={{ fontSize: 13, opacity: 0.8 }}>{error}</div>
            <button className="tad-btn tad-btn-primary" style={{ marginTop: 16 }} onClick={fetchAll}>Retry</button>
          </div>
        )}

        {!error && (
          <>
            {/* ── SUMMARY KPIs ── */}
            <div className="tad-section-title">📊 State Overview</div>
            <div className="tad-kpi-grid">
              {loadingSummary ? (
                Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)
              ) : summary ? (
                <>
                  <KpiCard icon="🏛️" value={summary.districtsCount}    label="Districts Participated"  color="#7c3aed" />
                  <KpiCard icon="🏫" value={summary.schoolsCount}       label="Schools Participated"    color="#0d9488" />
                  <KpiCard icon="📋" value={summary.registrationsCount} label="Total Events"     color="#2563eb" />
                  <KpiCard icon="🏆" value={summary.themesCount}        label="Themes Active"           color="#d97706" />
                  <KpiCard icon="🌱" value={summary.subThemesCount}     label="Sub-Themes Active"       color="#0891b2" />
                </>
              ) : (
                <div style={{ gridColumn: "1/-1", textAlign: "center", color: "#9ca3af", padding: 24 }}>
                  No summary data available for your state.
                </div>
              )}
            </div>

            {/* ── THEME-WISE ANALYSIS ── */}
            <div className="tad-section-title" style={{ marginTop: 32 }}>
              🌿 Theme-wise Analysis
              <div style={{ display: "inline-block", marginLeft: 16 }}>
                <input
                  id="state-theme-search"
                  placeholder="🔍 Search theme..."
                  value={themeSearch}
                  onChange={(e) => setThemeSearch(e.target.value)}
                  style={{
                    padding: "6px 14px", borderRadius: 20, border: "1.5px solid #e2e8f0",
                    fontSize: 13, outline: "none", minWidth: 200,
                  }}
                />
              </div>
            </div>

            {loadingThemes ? (
              <div className="tad-themes-grid">
                {Array.from({ length: 7 }).map((_, i) => <SkeletonCard key={i} />)}
              </div>
            ) : filteredThemes.length > 0 ? (
              <div className="tad-themes-grid">
                {filteredThemes.map((theme) => (
                  <StateThemeCard
                    key={theme.theme}
                    theme={theme}
                    totalRegs={themeData?.totalRegistrations || 1}
                    onDrillDown={(themeName) => {
                      navigate(`/admin/state/themes-analytics/theme/${encodeURIComponent(themeName)}`);
                    }}
                  />
                ))}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
                {themeSearch ? `No themes matching "${themeSearch}".` : "No theme data available for your state."}
              </div>
            )}

            {/* ── CHARTS ── */}
            {!loadingThemes && (
              <>
                <div className="tad-section-title" style={{ marginTop: 32 }}>📈 Visual Analytics</div>
                <div className="tad-charts-row">
                  {donutOptions && (
                    <div className="tad-chart-card">
                      <div className="tad-chart-title">🎯 Theme Distribution</div>
                      <ReactApexChart options={donutOptions.options} series={donutOptions.series} type="donut" height={320} />
                    </div>
                  )}
                  {schoolBarOptions && (
                    <div className="tad-chart-card">
                      <div className="tad-chart-title">🏫 Schools per Theme</div>
                      <ReactApexChart options={schoolBarOptions.options} series={schoolBarOptions.series} type="bar" height={320} />
                    </div>
                  )}
                </div>

                {/* Trend + District bar */}
                <div className="tad-charts-row" style={{ marginTop: 0 }}>
                  {trendOptions && (
                    <div className="tad-chart-card">
                      <div className="tad-chart-title">📅 Registration Trend (30 Days)</div>
                      <ReactApexChart options={trendOptions.options} series={trendOptions.series} type="area" height={240} />
                    </div>
                  )}
                  {districtBarOptions && (
                    <div className="tad-chart-card">
                      <div className="tad-chart-title">🏆 Top 10 Districts by Events</div>
                      <ReactApexChart options={districtBarOptions.options} series={districtBarOptions.series} type="bar" height={240} />
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ── DISTRICT-LEVEL ANALYSIS TABLE ── */}
            <div style={{ marginTop: 32 }}>
              <div className="tad-section-title">
                🏛️ District-Level Analysis
                <span style={{ fontSize: 13, fontWeight: 400, color: "#64748b", marginLeft: 8 }}>
                  (Click a district row to view school-wise data)
                </span>
              </div>

              {/* Search & Sort Controls */}
              <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
                <input
                  id="state-district-search"
                  placeholder="🔍 Search district..."
                  value={districtSearch}
                  onChange={(e) => setDistrictSearch(e.target.value)}
                  style={{
                    padding: "7px 16px", borderRadius: 20, border: "1.5px solid #e2e8f0",
                    fontSize: 13, outline: "none", minWidth: 220,
                  }}
                />
                <div style={{ display: "flex", gap: 6 }}>
                  {[
                    { key: "registrations", label: "By Events" },
                    { key: "schools",       label: "By Schools" },
                    { key: "themesCovered", label: "By Themes Covered" },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setDistrictSort(opt.key)}
                      style={{
                        padding: "7px 14px", borderRadius: 20, border: "1.5px solid",
                        borderColor: districtSort === opt.key ? "#0d9488" : "#e2e8f0",
                        background: districtSort === opt.key ? "#0d9488" : "#fff",
                        color: districtSort === opt.key ? "#fff" : "#374151",
                        fontSize: 12, cursor: "pointer", fontWeight: 500,
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {loadingDistrict ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
                  {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
                </div>
              ) : sortedDistricts.length > 0 ? (
                <div className="tad-table-card">
                  <table className="tad-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>District</th>
                        <th onClick={() => setDistrictSort("schools")} style={{ cursor: "pointer" }}>
                          Schools {districtSort === "schools" ? "▼" : ""}
                        </th>
                        <th onClick={() => setDistrictSort("registrations")} style={{ cursor: "pointer" }}>
                          Events {districtSort === "registrations" ? "▼" : ""}
                        </th>
                        <th onClick={() => setDistrictSort("themesCovered")} style={{ cursor: "pointer" }}>
                          Themes Covered {districtSort === "themesCovered" ? "▼" : ""}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDistricts.map((d, idx) => (
                        <tr
                          key={d.district}
                          onClick={() => handleDistrictClick(d.district)}
                          style={{ cursor: "pointer" }}
                          title={`Click to view schools in ${d.district}`}
                        >
                          <td><RankBadge idx={idx} /></td>
                          <td style={{ fontWeight: 600, color: "#2563eb" }}>🏛️ {d.district}</td>
                          <td>{fmt(d.schools)}</td>
                          <td style={{ fontWeight: 700, color: "#0d9488" }}>{fmt(d.registrations)}</td>
                          <td>
                            <span style={{
                              background: "#eff6ff", color: "#2563eb",
                              padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                            }}>
                              {d.themesCovered}/7
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
                  {districtSearch ? `No districts matching "${districtSearch}".` : "No district data available."}
                </div>
              )}
            </div>

            {/* ── AI INSIGHTS ── */}
            {!loadingThemes && themeData?.themes?.length > 0 && (() => {
              const withData = themeData.themes.filter((t) => t.registrations > 0);
              if (!withData.length) return null;
              const top = withData[0];
              const low = withData[withData.length - 1];
              return (
                <div className="tad-chart-card" style={{ marginTop: 28 }}>
                  <div className="tad-chart-title">🤖 State Insights</div>
                  <div style={{ fontSize: 14, color: "#6c757d", lineHeight: 1.8 }}>
                    <p>🏆 <strong style={{ color: "#1e293b" }}>{top.theme}</strong> leads in <strong style={{ color: top.color }}>{stateName}</strong> with{" "}
                      <strong style={{ color: top.color }}>{fmt(top.registrations)}</strong> events ({top.adoptionPct}% share).</p>
                    <p>🏫 <strong style={{ color: "#1e293b" }}>{fmt(summary?.schoolsCount)}</strong> schools across{" "}
                      <strong style={{ color: "#1e293b" }}>{fmt(summary?.districtsCount)}</strong> districts have participated in Mission LiFE themes.</p>
                    {withData.length >= 2 && (
                      <p>💡 <strong style={{ color: "#1e293b" }}>{low.theme}</strong> has the lowest adoption at{" "}
                        {low.adoptionPct}% — consider targeted outreach to boost participation in {stateName}.</p>
                    )}
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>

      {/* ── THEME DETAIL MODAL ── */}
      {selectedTheme && (
        <ThemeDetailModal
          theme={selectedTheme}
          onClose={() => setSelectedTheme(null)}
        />
      )}

      {/* ── DISTRICT DRILLDOWN MODAL ── */}
      {selectedDistrict && (
        <DistrictModal
          district={selectedDistrict}
          schools={loadingSchools ? [] : districtSchools}
          onClose={() => { setSelectedDistrict(null); setDistrictSchools([]); }}
        />
      )}

      {/* Loading overlay for district schools */}
      {loadingSchools && selectedDistrict && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 9998,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div className="tad-spinner" />
        </div>
      )}
    </div>
  );
}
